import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { useHistory } from 'react-router';
import _uniq from 'lodash/uniq';
import _pickBy from 'lodash/pickBy';
import { Button } from '@mui/material';

import { Wrapper, MainWrapper, Main, Aside, AsideWrapper } from './Content.styles.js';
import Header from 'pages/Settings/containers/Persons/Card/components/Header/Header';
import ContentWrapper from 'pages/Settings/containers/Persons/Card/components/Content/ContentWrapper/ContentWrapper';
import { formScheme } from '../form';
import { getExternalNationalityCodesData } from 'reactStore/selectors/externalNationalityCodesSelector';
import moment from 'utils/moment';
import { toastify } from 'utils/helpers';
import { createPerson, updatePerson } from 'reactStore/slices/personsSlice';

const Content = ({ personData, isGuest, isAddEmployeePage = false }) => {
  const dispatch = useDispatch();
  const isManager = personData.role === 'manager';
  const isSelfBooker = personData.role === 'self_booker';
  const [checkSelectedOptionsUser, setCheckSelectedOptionsUser] = useState(
    isAddEmployeePage || isManager || isSelfBooker ? ['user'] : [],
  );

  const [checkSelectedOptionsContactPerson, setCheckSelectedOptionsContactPerson] = useState(
    personData.is_contact_person && personData.contact_email ? ['contactPerson'] : [],
  );
  const [checkSelectedOptionsTraveller, setCheckSelectedOptionsTraveller] = useState(
    personData.can_travelling ? ['canTravelling'] : [],
  );
  const { t } = useTranslation();
  const history = useHistory();
  const externalNationalityCodesData = useSelector(getExternalNationalityCodesData, shallowEqual);

  const personNationalityCodeId = useMemo(() => {
    return externalNationalityCodesData?.find((item) => item.value === personData?.nationality?.value);
  }, [externalNationalityCodesData, personData?.nationality?.value])?.id;

  const defaultValueForPerson = useMemo(() => {
    return {
      first_name: personData?.first_name ?? '',
      last_name: personData?.last_name ?? '',
      middle_name: personData?.middle_name?.trim() ?? '',
      role: personData?.role,
      phone: personData?.phone,
      gender: personData?.gender,
      email: personData?.email,
      date_of_birth: personData?.date_of_birth
        ? moment(personData?.date_of_birth).utcOffset(personData?.date_of_birth).format('DD.MM.YYYY')
        : '',
      nationality_code_id: personNationalityCodeId,
      documents_attributes: personData?.documents?.length
        ? [...personData?.documents].map((item) => {
            return {
              ...item,
              elapsed_time: item?.elapsed_time ? moment(item?.elapsed_time).format('DD.MM.YYYY') : '',
              _destroy: 0,
            };
          })
        : [
            {
              _type: '',
              number: '',
              elapsed_time: '',
              last_name: '',
              first_name: '',
              middle_name: '',
              _destroy: 0,
            },
          ],
    };
  }, [personNationalityCodeId, personData]);

  const { errors, control, handleSubmit, setValue } = useForm({
    ...formScheme.initialScheme(
      checkSelectedOptionsUser.includes('user'),
      checkSelectedOptionsContactPerson.includes('contactPerson'),
      checkSelectedOptionsTraveller.includes('canTravelling'),
    ),
    defaultValues: {
      person: Array.from(Array(parseInt('1')), () => defaultValueForPerson),
    },
  });

  useEffect(() => {
    if (personNationalityCodeId) {
      setValue('person', [{ ...defaultValueForPerson, nationality_code_id: personNationalityCodeId }]);
    }
  }, [personNationalityCodeId]);

  useEffect(() => {
    if (errors.person?.length) {
      const messages = errors.person?.reduce((result, person) => {
        const text = [...Object.keys(person)].reduce((result, item) => {
          if (item === 'documents_attributes') {
            return [
              ...result,
              ...person[item].reduce((result, documentItem) => {
                const docText = [...Object.keys(documentItem)].reduce((result, item) => {
                  return [...result, documentItem[item]?.message];
                }, []);
                return [...result, ...docText];
              }, []),
            ];
          }
          return [...result, person[item]?.message];
        }, []);
        return [...result, ...text];
      }, []);
      _uniq(messages).map((message) => {
        toastify('error', message);
      });
    }
  }, [errors]);

  const checkJustOneRussianPassport = (documents) => {
    const count = documents?.reduce((result, document) => {
      if (document._type === 'RussianPassport' && !document._destroy) {
        return result + 1;
      }
      return result;
    }, 0);
    return count <= 1;
  };

  const checkForeignAndInternationalDocuments = (documents) => {
    const result = documents?.reduce((result, item) => {
      if (result.some((resultItem) => item._type === resultItem._type && item.number === resultItem.number)) {
        return [...result, false];
      }
      return [...result, item];
    }, []);
    return !result.includes(false);
  };

  const handleBook = handleSubmit(async ({ person }) => {
    const [user] = person;
    const personNationality = externalNationalityCodesData?.find((item) => item.id === user?.nationality_code_id);
    const result = {
      date_of_birth: user.date_of_birth,
      first_name: user.first_name,
      middle_name: user.middle_name || ' ',
      gender: user.gender,
      last_name: user.last_name,
      phone: user.phone,
      role: user.role,
      can_travelling: checkSelectedOptionsTraveller.includes('canTravelling'),
    };

    if (checkSelectedOptionsTraveller.includes('canTravelling')) {
      result.documents_attributes = user.documents_attributes?.reduce((result, item) => {
        // _destroy бэк проверяет как 1(число) для удаления документа. 0 стоит валидации у остальных как заглушка.
        return [
          ...result,
          _pickBy(item, (value, field) => {
            if (field !== 'doctype') {
              return value.length || value === 1;
            }
            return false;
          }),
        ];
      }, []);
    }

    if (user.nationality_code_id) {
      result.nationality_attributes = {
        value: personNationality?.value,
        text: personNationality?.title,
      };
    }

    if (personData.id) {
      result.person_id = personData.id;
    }

    if (checkSelectedOptionsUser.includes('user')) {
      result.email = user.email;
    }

    if (checkSelectedOptionsContactPerson.includes('contactPerson')) {
      result.is_contact_person = true;
      result.contact_email = user.contact_email;
    }

    if (
      checkJustOneRussianPassport(user.documents_attributes) &&
      checkForeignAndInternationalDocuments(user.documents_attributes)
    ) {
      const { payload, error } = personData.id
        ? await dispatch(
            updatePerson({
              data: {
                ...result,
                id: personData.id,
              },
            }),
          )
        : await dispatch(
            createPerson({
              data: {
                ...result,
                id: personData.id,
              },
            }),
          );

      if (!payload.error) {
        history.goBack();
      } else {
        toastify('error', 'Произошла ошибка, проверьте заполнение документов');
      }
      if (error) {
        toastify(
          'error',
          'Можно использовать только один паспорт РФ, также данные Иностранных/Заграничных паспортов не должны совпадать',
        );
      }
    }
  });

  return (
    <Wrapper>
      <Header
        firstName={personData?.first_name}
        middleName={personData?.middle_name}
        lastName={personData?.last_name}
        role={personData?.role}
        office={personData?.office?.name}
        isAddEmployeePage={isAddEmployeePage}
      />
      <MainWrapper>
        <Main>
          <Controller
            as={ContentWrapper}
            control={control}
            name="person"
            error={errors?.person}
            isGuest={isGuest}
            isAddEmployeePage={isAddEmployeePage}
            checkSelectedOptionsUser={checkSelectedOptionsUser}
            setCheckSelectedOptionsUser={setCheckSelectedOptionsUser}
            checkSelectedOptionsContactPerson={checkSelectedOptionsContactPerson}
            setCheckSelectedOptionsContactPerson={setCheckSelectedOptionsContactPerson}
            checkSelectedOptionsTraveller={checkSelectedOptionsTraveller}
            setCheckSelectedOptionsTraveller={setCheckSelectedOptionsTraveller}
          />
        </Main>
        <Aside>
          <AsideWrapper>
            <Button type="submit" color="primary" variant="primary" onClick={handleBook}>
              {t('Settings.Person.Card.Save')}
            </Button>
            <Button variant="secondary" type="submit" onClick={() => history.goBack()}>
              {t('Settings.Person.Card.Cancel')}
            </Button>
          </AsideWrapper>
        </Aside>
      </MainWrapper>
    </Wrapper>
  );
};

Content.propTypes = {
  isGuest: PropTypes.bool,
  isAddEmployeePage: PropTypes.bool,
  personData: PropTypes.object,
};

export default Content;
